import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { ACCION_TIPOS } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { X } from "lucide-react";

const TIPO_KEY = {
  intercambio_estudiantil: "tipo_intercambio",
  pasantia_docente: "tipo_pasantia",
  proyecto_investigacion: "tipo_proyecto",
  publicacion_conjunta: "tipo_publicacion",
  cotutela_tesis: "tipo_cotutela",
  evento_seminario: "tipo_evento",
  visita_institucional: "tipo_visita",
  otro: "tipo_otro",
};

export default function AccionForm({ convenio, onClose, onCreated, defaultConvenioId }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [convenios, setConvenios] = useState([]);
  const [form, setForm] = useState({
    convenio_id: convenio?.id || defaultConvenioId || "",
    tipo: "intercambio_estudiantil",
    tipo_otro_descripcion: "",
    facultad_id: user?.facultad_id || "",
    semestre: "1",
    anio: new Date().getFullYear(),
    num_participantes: 1,
    direccion: "n_a",
    observaciones: "",
  });
  const [facultades, setFacultades] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!convenio) {
      api.get("/convenios").then((r) => setConvenios(r.data));
    }
    api.get("/facultades").then((r) => setFacultades(r.data));
  }, [convenio]);

  const selectedConvenio = convenio || convenios.find((c) => c.id === form.convenio_id);
  const tipoConfig = ACCION_TIPOS.find((tp) => tp.value === form.tipo);

  useEffect(() => {
    if (selectedConvenio && !form.facultad_id && user?.role !== "facultad") {
      const ids = selectedConvenio.facultad_ids || [];
      if (ids.length === 1) setForm((f) => ({ ...f, facultad_id: ids[0] }));
    }
    // eslint-disable-next-line
  }, [selectedConvenio?.id]);

  const availableFacultades = selectedConvenio
    ? facultades.filter((f) => (selectedConvenio.facultad_ids || []).includes(f.id))
    : facultades;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.convenio_id) return toast.error(t("af.err_convenio"));
    if (!form.facultad_id) return toast.error(t("af.err_facultad"));
    if (form.tipo === "otro" && !form.tipo_otro_descripcion) return toast.error(t("af.err_otro"));

    setSaving(true);
    try {
      await api.post("/acciones", {
        ...form,
        anio: Number(form.anio),
        num_participantes: Number(form.num_participantes),
      });
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-end lg:justify-center" onClick={onClose} data-testid="accion-form-modal">
      <div
        className="bg-white w-full lg:max-w-2xl lg:mx-6 lg:my-6 max-h-[92vh] overflow-y-auto p-6 sm:p-8 lg:p-10 border border-zinc-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="section-marker">{t("af.kicker")}</div>
            <h2 className="text-2xl sm:text-3xl font-medium tracking-tight mt-1">{t("af.title")}</h2>
            {selectedConvenio && (
              <p className="text-sm text-zinc-500 mt-2">
                {t("af.under")} <strong>{selectedConvenio.institucion_nombre}</strong>
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-950" data-testid="close-accion-form"><X size={20} strokeWidth={1.5} /></button>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="accion-form">
          {!convenio && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.convenio_label")}</label>
              <select required value={form.convenio_id} onChange={set("convenio_id")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="acc-convenio">
                <option value="">{t("af.select_default")}</option>
                {convenios.map((c) => (
                  <option key={c.id} value={c.id}>{c.institucion_nombre} · {c.institucion_pais}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.tipo_label")}</label>
            <select required value={form.tipo} onChange={set("tipo")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="acc-tipo">
              {ACCION_TIPOS.map((tp) => (
                <option key={tp.value} value={tp.value}>{t(`af.${TIPO_KEY[tp.value]}`)}</option>
              ))}
            </select>
          </div>

          {form.tipo === "otro" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.otro_desc_label")}</label>
              <input value={form.tipo_otro_descripcion} onChange={set("tipo_otro_descripcion")} className="w-full border border-zinc-300 px-4 py-3" data-testid="acc-otro-desc" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.semestre")}</label>
              <select value={form.semestre} onChange={set("semestre")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="acc-semestre">
                <option value="1">{t("af.sem_1")}</option>
                <option value="2">{t("af.sem_2")}</option>
                <option value="verano">{t("af.sem_verano")}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.anio")}</label>
              <input type="number" min="2000" max="2100" value={form.anio} onChange={set("anio")} className="w-full border border-zinc-300 px-4 py-3" data-testid="acc-anio" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.participantes")}</label>
              <input type="number" min="0" value={form.num_participantes} onChange={set("num_participantes")} className="w-full border border-zinc-300 px-4 py-3" data-testid="acc-participantes" />
            </div>
            {tipoConfig?.dir && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.direccion")}</label>
                <select value={form.direccion} onChange={set("direccion")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="acc-direccion">
                  <option value="n_a">{t("af.dir_na")}</option>
                  <option value="entrante">{t("af.dir_entrante")}</option>
                  <option value="saliente">{t("af.dir_saliente")}</option>
                </select>
              </div>
            )}
          </div>

          {user?.role !== "facultad" && (
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.facultad")}</label>
              <select required value={form.facultad_id} onChange={set("facultad_id")} className="w-full border border-zinc-300 px-4 py-3 bg-white" data-testid="acc-facultad">
                <option value="">{t("af.select_default")}</option>
                {availableFacultades.map((f) => (
                  <option key={f.id} value={f.id}>{f.nombre}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("af.obs")}</label>
            <textarea value={form.observaciones} onChange={set("observaciones")} rows={2} className="w-full border border-zinc-300 px-4 py-3 resize-none" data-testid="acc-obs" />
          </div>

          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">
              {t("af.cancel")}
            </button>
            <button type="submit" disabled={saving} className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50" data-testid="acc-submit">
              {saving ? t("af.saving") : t("af.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
